import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useAlertsSummary } from "@/hooks/use-analytics";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import { formatNumber, daysAgo, today } from "@/lib/utils";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { FilterBar } from "@/components/dashboard/FilterBar";
import { HelpIcon } from "@/components/HelpTooltip";
import type { ProductAlertSummary } from "@shared/types";

function cpkRating(cpk: number | null): { label: string; color: string } {
  if (cpk == null) return { label: "N/A", color: "text-muted-foreground" };
  if (cpk >= 1.67) return { label: "Excellent", color: "text-emerald-400" };
  if (cpk >= 1.33) return { label: "Capable", color: "text-emerald-400" };
  if (cpk >= 1.0) return { label: "Marginal", color: "text-amber-400" };
  return { label: "Incapable", color: "text-red-400" };
}

function AlertBadge({ count, type }: { count: number; type: "critical" | "warning" | "at_risk" }) {
  if (count === 0) return <span className="text-muted-foreground">0</span>;
  const colors = {
    critical: "bg-red-500/20 text-red-400",
    warning: "bg-purple-500/20 text-purple-400",
    at_risk: "bg-amber-500/20 text-amber-400",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${colors[type]}`}>
      {count}
    </span>
  );
}

export function Insights() {
  useSetPageHeader({
    title: "Insights",
    subtitle: "Aggregated quality alerts across all products",
    icon: Sparkles,
  });

  const [from, setFrom] = useState(() => daysAgo(90));
  const [to, setTo] = useState(() => today());
  const alerts = useAlertsSummary(from, to);

  return (
    <div className="space-y-6">
      <FilterBar from={from} to={to} onFromChange={setFrom} onToChange={setTo} />

      {alerts.isLoading ? (
        <DashboardSkeleton cards={4} chart={false} table={true} />
      ) : alerts.error ? (
        <div className="flex h-40 items-center justify-center text-destructive">
          Error: {alerts.error.message}
        </div>
      ) : alerts.data ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <SummaryCard
              label="Critical Alerts"
              value={alerts.data.totals.critical}
              color={alerts.data.totals.critical > 0 ? "text-red-400" : "text-emerald-400"}
            />
            <SummaryCard
              label="Warnings"
              value={alerts.data.totals.warning}
              color={alerts.data.totals.warning > 0 ? "text-purple-400" : "text-emerald-400"}
            />
            <SummaryCard
              label="At Risk Predictions"
              value={alerts.data.totals.at_risk}
              color={alerts.data.totals.at_risk > 0 ? "text-amber-400" : "text-emerald-400"}
            />
            <SummaryCard
              label="Products Scanned"
              value={alerts.data.totals.products_scanned}
              color="text-card-foreground"
            />
          </div>

          {/* Products table */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="mb-3 text-lg font-semibold text-card-foreground">
              Product Health — 28-Day Strength
            </h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Cpk measures process capability. &gt;1.67 = Excellent, &gt;1.33 = Capable, 1.0–1.33 = Marginal, &lt;1.0 = Incapable (action needed)
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-3 py-2 text-muted-foreground font-medium">Product</th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Samples</th>
                    <th className="px-3 py-2 text-center text-muted-foreground font-medium">Critical<HelpIcon k="nelson_rules" align="center" /></th>
                    <th className="px-3 py-2 text-center text-muted-foreground font-medium">Warnings<HelpIcon k="nelson_rules" align="center" /></th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Cpk<HelpIcon k="cpk" align="right" /></th>
                    <th className="px-3 py-2 text-muted-foreground font-medium">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.data.products.map((p: ProductAlertSummary) => {
                    const rating = cpkRating(p.cpk);
                    const hasIssues = p.nelson_critical > 0;
                    return (
                      <tr
                        key={p.slug}
                        className={`border-b border-border/50 hover:bg-muted/50 ${
                          hasIssues ? "bg-red-500/5" : ""
                        }`}
                      >
                        <td className="px-3 py-2">
                          <Link
                            to={`/quality?product=${p.slug}`}
                            className="font-medium text-foreground hover:text-primary hover:underline"
                          >
                            {p.label}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {formatNumber(p.sample_count)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <AlertBadge count={p.nelson_critical} type="critical" />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <AlertBadge count={p.nelson_warning} type="warning" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-medium ${rating.color}`}>
                            {p.cpk != null ? p.cpk.toFixed(2) : "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs ${rating.color}`}>{rating.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {alerts.data.products.length === 0 && (
              <p className="mt-4 text-center text-muted-foreground">
                No products with 28-day strength data in this date range
              </p>
            )}
          </div>

          {/* Products needing attention */}
          {alerts.data.products.filter((p) => p.nelson_critical > 0).length > 0 && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5">
              <h2 className="mb-3 text-lg font-semibold text-red-400">
                Needs Attention
              </h2>
              <div className="space-y-2">
                {alerts.data.products
                  .filter((p) => p.nelson_critical > 0)
                  .map((p) => (
                    <div key={p.slug} className="flex items-center justify-between rounded-lg border border-border p-3">
                      <div>
                        <Link
                          to={`/quality?product=${p.slug}`}
                          className="font-medium text-foreground hover:text-primary hover:underline"
                        >
                          {p.label}
                        </Link>
                        <div className="flex gap-3 text-xs text-muted-foreground">
                          {p.nelson_critical > 0 && (
                            <span className="text-red-400">
                              {p.nelson_critical} critical alert{p.nelson_critical > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      {p.cpk != null && (
                        <div className="text-right">
                          <p className={`text-sm font-medium ${cpkRating(p.cpk).color}`}>
                            Cpk {p.cpk.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{formatNumber(value)}</p>
    </div>
  );
}
